import type { BlockPhase, LocalDate } from '@cadencia/contracts';
import { compareLocalDates, daysOfWeek } from '../units/local-date.js';
import { isSignificantLowerBody, type WeekSession } from './week-reorganization.js';

/**
 * Prescripcion de cardio (entregable 3.9).
 *
 * AVISO DE HONESTIDAD, y es grande: el documento de entrenamiento no tiene
 * seccion de cardio. Solo hay tres frases repartidas —§3 para la readaptacion
 * («2-3 sesiones/semana de carrera continua facil, 20-35 min, creciente»), §6.3
 * («cardio duro cancelado» con readiness <35) y §8 (no la vispera de una pierna
 * importante)— y el modelo de datos con las dos tablas.
 *
 * Todo lo que va por fase debajo de esas tres frases es CONVENCION DECLARADA.
 * Esta escrito en un sitio, con los numeros a la vista, para que se pueda
 * discutir y cambiar sin buscarlo por el codigo.
 *
 * Modulo puro: no lee el reloj ni la base de datos (ADR-007).
 */

// ---------------------------------------------------------------------------
// Vocabulario (docs/DATA_MODEL.md §11)
// ---------------------------------------------------------------------------

export type CardioModality = 'continuous_run';
export type CardioIntensity = 'easy' | 'moderate' | 'threshold';

export interface CardioWeekPlan {
  sessionsPerWeek: number;
  /** Minutos por sesion en ESTA semana del bloque. */
  minutes: number;
  modality: CardioModality;
  intensity: CardioIntensity;
  reasonCodes: string[];
}

// ---------------------------------------------------------------------------
// Convenciones por fase
// ---------------------------------------------------------------------------

interface CardioRamp {
  sessions: readonly [number, number];
  /** Minutos por sesion: [primera semana, ultima]. */
  minutes: readonly [number, number];
  intensity: CardioIntensity;
  rationale: string;
}

/**
 * Solo la readaptacion sale del documento (§3). Las otras cinco son convencion.
 *
 * El criterio que las ordena: el cardio compite por la misma recuperacion que
 * la fuerza. Donde el bloque acumula volumen de fuerza se prescribe el minimo
 * que sostiene la salud cardiovascular; donde el bloque no esta acumulando
 * —mantenimiento, transicion— cabe algo mas. Cadencia no prescribe nutricion,
 * asi que el cardio de aqui no persigue ningun gasto calorico objetivo: es
 * trabajo aerobico de salud, y en exceso se paga en la sesion del dia
 * siguiente.
 */
export const CARDIO_BY_PHASE: Record<BlockPhase, CardioRamp> = {
  readaptation: {
    // Del §3, literal.
    sessions: [2, 3],
    minutes: [20, 35],
    intensity: 'easy',
    rationale: 'Reintroducir trabajo aeróbico sin competir con la readaptación de fuerza',
  },
  // Heredada: no la genera nadie, la leen los bloques guardados con ese valor.
  fat_loss: {
    sessions: [2, 3],
    minutes: [25, 35],
    intensity: 'easy',
    rationale: 'Trabajo aeróbico de salud sin competir con la recuperación',
  },
  recomposition: {
    sessions: [2, 3],
    minutes: [25, 35],
    intensity: 'easy',
    rationale: 'Mantener capacidad aeróbica mientras el foco está en la fuerza',
  },
  muscle_gain: {
    sessions: [2, 2],
    minutes: [20, 25],
    intensity: 'easy',
    rationale: 'Mínimo que sostiene la salud cardiovascular sin restar recuperación',
  },
  // Acumula igual que `muscle_gain`, asi que el cardio se queda en el minimo:
  // la recuperacion que sobra va al musculo priorizado.
  specialization: {
    sessions: [2, 2],
    minutes: [20, 30],
    intensity: 'easy',
    rationale: 'Mínimo que sostiene la salud cardiovascular sin restar recuperación al énfasis',
  },

  maintenance: {
    sessions: [2, 3],
    minutes: [25, 35],
    intensity: 'easy',
    rationale: 'Sostener la base aeróbica con poco coste',
  },
  transition: {
    sessions: [3, 3],
    minutes: [30, 40],
    intensity: 'easy',
    rationale: 'Descargar la fuerza manteniendo actividad',
  },
};

/** Readiness por debajo del cual se cancela el cardio duro (§6.3). */
export const CARDIO_HARD_CANCEL_READINESS = 35;

/**
 * Minutos que se recortan cuando el readiness esta bajo pero no en el suelo.
 *
 * CONVENCION declarada: el §6.3 no dice nada del cardio en la banda 35-49, pero
 * recorta un 10 % la carga y quita una serie de accesorios. Recortar tambien el
 * cardio es coherente con esa linea; dejarlo intacto seria tratar la carrera
 * como si no costara nada.
 */
export const CARDIO_LOW_READINESS = 50;
export const CARDIO_LOW_READINESS_FACTOR = 0.7;

// ---------------------------------------------------------------------------
// Plan semanal
// ---------------------------------------------------------------------------

function interpolar(desde: number, hasta: number, t: number): number {
  return desde + (hasta - desde) * t;
}

export interface CardioPlanInput {
  phase: BlockPhase;
  /** Semana dentro del bloque, empezando en 1. */
  weekIndex: number;
  plannedWeeks: number;
}

/**
 * Cuanto cardio toca esta semana.
 *
 * Creciente a lo largo del bloque, que es lo unico que el §3 pide de forma
 * explicita («20-35 min, creciente»).
 */
export function planWeeklyCardio(input: CardioPlanInput): CardioWeekPlan {
  const ramp = CARDIO_BY_PHASE[input.phase];
  const total = Math.max(1, input.plannedWeeks);
  const posicion = Math.min(Math.max(input.weekIndex, 1), total);
  const t = total <= 1 ? 0 : (posicion - 1) / (total - 1);

  return {
    sessionsPerWeek: Math.round(interpolar(ramp.sessions[0], ramp.sessions[1], t)),
    minutes: Math.round(interpolar(ramp.minutes[0], ramp.minutes[1], t) / 5) * 5,
    modality: 'continuous_run',
    intensity: ramp.intensity,
    reasonCodes: [`cardio_${input.phase}`],
  };
}

// ---------------------------------------------------------------------------
// Colocacion en la semana
// ---------------------------------------------------------------------------

export interface CardioSlot {
  date: LocalDate;
  minutes: number;
  modality: CardioModality;
  intensity: CardioIntensity;
  rationale: string;
}

export interface PlaceCardioInput {
  weekStart: LocalDate;
  plan: CardioWeekPlan;
  /** Sesiones de fuerza ya colocadas en la semana. */
  strengthSessions: readonly WeekSession[];
  phase: BlockPhase;
  /** No se colocan sesiones antes de este dia. Opcional. */
  notBefore?: LocalDate | undefined;
}

/**
 * Reparte las sesiones de cardio por la semana.
 *
 * Dos reglas, y la segunda es la del §8: no la vispera de una sesion de pierna
 * importante. Correr fuerte el dia antes se paga en la pierna, no en la
 * carrera: se llega con los isquios cargados y se entrena peor lo que mas
 * cuesta recuperar.
 *
 * La primera es preferir los dias SIN fuerza. `PRODUCT_SPEC.md` describe el
 * cardio como «carrera continua en días sin fuerza», asi que un dia libre vale
 * mas que uno compartido. Si no quedan libres suficientes, se comparte dia
 * antes que renunciar a la sesion: compartir cuesta tiempo, no recuperacion.
 */
export function placeCardio(input: PlaceCardioInput): CardioSlot[] {
  const ocupados = new Map<LocalDate, WeekSession[]>();
  for (const sesion of input.strengthSessions) {
    ocupados.set(sesion.scheduledDate, [
      ...(ocupados.get(sesion.scheduledDate) ?? []),
      sesion,
    ]);
  }

  const dias = daysOfWeek(input.weekStart).filter(
    (dia) => input.notBefore === undefined || compareLocalDates(dia, input.notBefore) >= 0,
  );

  const vispera = (dia: LocalDate): boolean => {
    const siguiente = daysOfWeek(input.weekStart).find(
      (candidato) => compareLocalDates(candidato, dia) > 0,
    );
    // El ultimo dia de la semana no tiene vispera dentro de ella. Es una
    // limitacion conocida: no se mira la pierna del lunes siguiente.
    if (siguiente === undefined) return false;
    return (ocupados.get(siguiente) ?? []).some((sesion) =>
      isSignificantLowerBody(sesion.volumeByMuscle),
    );
  };

  const libres = dias.filter((dia) => !ocupados.has(dia) && !vispera(dia));
  const compartidos = dias.filter((dia) => ocupados.has(dia) && !vispera(dia));

  // Los libres primero, y dentro de cada grupo en orden de calendario: repartir
  // el cardio por la semana es preferible a amontonarlo.
  const elegidos = [...libres, ...compartidos].slice(0, Math.max(0, input.plan.sessionsPerWeek));

  return elegidos
    .sort((a, b) => compareLocalDates(a, b))
    .map((date) => ({
      date,
      minutes: input.plan.minutes,
      modality: input.plan.modality,
      intensity: input.plan.intensity,
      rationale: CARDIO_BY_PHASE[input.phase].rationale,
    }));
}

// ---------------------------------------------------------------------------
// Modulacion por readiness (§6.3)
// ---------------------------------------------------------------------------

export interface CardioTodayDecision {
  /** `null` = hoy no se hace. */
  slot: CardioSlot | null;
  reasonCodes: string[];
}

/**
 * Ajusta el cardio de HOY segun el readiness.
 *
 * El §6.3 solo dice una cosa del cardio, y es tajante: con readiness <35, el
 * cardio duro se cancela. Un cardio facil no se cancela —caminar o trotar suave
 * con readiness bajo no agrava nada y a veces ayuda— pero se recorta.
 */
export function cardioForToday(
  slot: CardioSlot,
  readinessToday: number | null,
): CardioTodayDecision {
  if (readinessToday === null) {
    // Sin check-in no se supone fatiga. Inventar una cancelaria entrenamientos
    // por no haber rellenado un formulario.
    return { slot, reasonCodes: ['no_readiness_checkin'] };
  }

  if (readinessToday < CARDIO_HARD_CANCEL_READINESS) {
    if (slot.intensity !== 'easy') {
      return { slot: null, reasonCodes: ['hard_cardio_cancelled_low_readiness'] };
    }
    return {
      slot: { ...slot, minutes: Math.round((slot.minutes * CARDIO_LOW_READINESS_FACTOR) / 5) * 5 },
      reasonCodes: ['easy_cardio_shortened_low_readiness'],
    };
  }

  if (readinessToday < CARDIO_LOW_READINESS) {
    return {
      slot: {
        ...slot,
        intensity: 'easy',
        minutes: Math.round((slot.minutes * CARDIO_LOW_READINESS_FACTOR) / 5) * 5,
      },
      reasonCodes: ['cardio_reduced_low_readiness'],
    };
  }

  return { slot, reasonCodes: ['cardio_as_planned'] };
}
